import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import * as Scrivito from "scrivito";

import * as Scroll from "react-scroll";
import { getNewsCategories } from "^/utils/getNewsCategories";
import moment from "moment";
import Story from "^/Components/StoriesPreview/Story";
import AddButton from "^/Components/AddButton";
import setLanguage from "^/utils/setLanguage";
import PageHeadBlock from "^/Components/StoriesPreview/PageHeadBlock";
import FetchMoreButton from "^/Components/FetchMoreButton";
import I18n from "^/config/i18n";
import basicNewsQuery from "^/utils/basicNewsQuery";
import FilterForm from "./FilterForm";

const NewsOverviewPage = ({ page }) => {
  setLanguage(page);
  const siteId = page.siteId();
  const lang = page.language();
  const pageLength = page.get("newsCountPerPage") || 30;
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(false);
  const pageNumberRef = useRef(0);
  const [searchTotal, setSearchTotal] = useState(0);
  const currentPageParams = Scrivito.currentPageParams();
  const { category, order, q: query } = currentPageParams;
  const currentHour = useMemo(() => moment().startOf("day").toDate(), []);
  const filter = page.get("categoryFilter");

  const fetchMoreData = useCallback(
    (cat, sort, q) => {
      setFetching(true);
      let allQuery = basicNewsQuery(
        siteId,
        currentHour,
        pageNumberRef.current * pageLength
      ).and("displayOnThisPage", "refersTo", page);

      if (cat && cat !== "") {
        allQuery = allQuery.andFullTextOf(
          "customizableCategories",
          "contains",
          cat
        );
      }
      const [field, direction] = sort?.split(".") || [
        "publishFromDate",
        "desc",
      ];
      allQuery = allQuery.order(field, direction);
      if (q && q !== "") {
        allQuery = allQuery.andFullTextOf("*", "containsPrefix", q);
      }

      Scrivito.load(() => {
        const response = allQuery.take(pageLength);
        const count = allQuery.count();
        return {
          searchCount: count,
          searchItems: response,
        };
      }).then((data) => {
        const { searchItems, searchCount } = data;
        setItems((prev) => [...prev, ...searchItems]);
        pageNumberRef.current += 1;
        setSearchTotal(searchCount);
        setFetching(false);
      });
    },
    [currentHour, page, pageLength, siteId]
  );

  useEffect(() => {
    fetchMoreData(category, order, query);
  }, [category, fetchMoreData, order, query]);

  useEffect(() => {
    getNewsCategories().then((cats) => {
      setCategories(cats);
    });
  }, []);

  const addNewsPage = async () => {
    const publishUntilDate = new Date();
    publishUntilDate.setDate(publishUntilDate.getDate() + 365);
    const newPage = Scrivito.getClass("NewsPage").create({});
    newPage.update({
      _path: `${page.path()}/${newPage.id()}`,
      _siteId: siteId,
      _language: lang,
      publishFromDate: new Date(),
      publishUntilDate,
      displayOnThisPage: [page],
    });
    await newPage.finishSaving();
    Scrivito.navigateTo(newPage);
  };

  const handleParamsChange = (name, value) => {
    setItems([]);
    pageNumberRef.current = 0;
    Scrivito.navigateTo(page, {
      params: { ...currentPageParams, ...{ [name]: value } },
      hash: "results",
    });
  };

  return (
    <div className="overflow-hidden">
      <PageHeadBlock page={page} />
      <Scroll.Element id="afterHero" />
      <div className="container s-py-5">
        <div className="row" id="results">
          <div className="col-sm-8">
            <AddButton onClick={addNewsPage} title="Add new news page" />
            {items.length === 0 && (
              <div className="alert alert-warning">
                {I18n.t("No news found", { ns: "live" })}
              </div>
            )}
            {items.map((story) => (
              <Story story={story} key={story.id()} pageType={"NewsPage"} />
            ))}
            <FetchMoreButton
              show={searchTotal > items.length}
              fetching={fetching}
              onClick={() => fetchMoreData(category, order, query)}
              language={lang}
            />
          </div>
          <div className="col-sm-4">
            <FilterForm
              lang={lang}
              updateSearch={(event) => {
                handleParamsChange("q", event.target.value);
              }}
              updateCategory={(event) => {
                handleParamsChange("category", event.target.value);
              }}
              updateOrder={(event) => {
                handleParamsChange("order", event.target.value);
              }}
              filter={filter}
              categories={categories}
              currentValues={currentPageParams}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
Scrivito.provideComponent(
  "NewsOverviewPage",
  Scrivito.connect(NewsOverviewPage)
);
